import {
    partitionAtFirst,
    startsWithCapital,
} from '@precon-tools-suite-by-shared/js-utils';

/**
 * User name format in an access token.
 */
export type UserNameFromToken = {
    given_name?: string;
    family_name?: string;
    display_name?: string;
};

/**
 * User name format when retrieved from a GraphQL node.
 */
export type UserNameFromNode = {
    first?: string;
    last?: string | { main?: string; prefix?: string; full?: string };
    short?: string;
    full?: string;
    username?: string;
};

/**
 * Possible types of username inputs.
 */
export type UserNameInput =
    | string
    | NormalizedUserName
    | UserNameFromToken
    | UserNameFromNode
    | null
    | undefined;

/**
 * The normalized form of a last name.
 */
export type NormalizedLastName = {
    main: string | null;
    prefix: string | null;
    full: string | null;
};

/**
 * The normalized username format when used in the library.
 */
export type NormalizedUserName = {
    first: string | null;
    last: NormalizedLastName;
    short: string | null;
    full: string | null;
    username: string | null;
};

export function isNormalizedUserName(
    value: unknown
): value is NormalizedUserName {
    return (
        !!value &&
        typeof value === 'object' &&
        'first' in value &&
        (value.first === null || typeof value.first === 'string') &&
        'short' in value &&
        (value.short === null || typeof value.short === 'string') &&
        'last' in value &&
        typeof value.last === 'object' &&
        'full' in value &&
        (value.full === null || typeof value.full === 'string') &&
        'username' in value &&
        (value.username === null || typeof value.username === 'string')
    );
}

/**
 * Splits the string on the spaces.
 *
 * It returns an empty array if the string was empty.
 */
export function splitString(input: string | string[]): string[] {
    if (typeof input === 'string') input = input.trim().split(' ');
    return input.filter((str) => str !== '');
}

/**
 * Makes a new empty last name.
 */
export function makeEmptyLastName(): NormalizedLastName {
    return {
        main: null,
        prefix: null,
        full: null,
    };
}

/**
 * Makes a new empty username.
 */
export function makeEmptyUserName(): NormalizedUserName {
    return {
        first: null,
        last: makeEmptyLastName(),
        short: null,
        full: null,
        username: null,
    };
}

/**
 * Tries to make a normalized username from a string.
 */
export function splitFullNameString(input: string): NormalizedUserName {
    const parts = splitString(input);

    if (parts.length == 0) return makeEmptyUserName();

    // Just one part is interpreted as a first name.
    if (parts.length == 1)
        return {
            first: parts[0],
            last: makeEmptyLastName(),
            short: input,
            full: input,
            username: input,
        };

    // When we have more parts, we interpret the first as the first name.
    const first = parts.shift() as string;
    const last = splitLastNameString(parts);
    const full = [first, last.full].join(' ');

    return {
        first,
        last,
        short: first,
        full,
        username: full,
    };
}

/**
 * Splits a last name into a main and prefix part.
 */
export function splitLastNameString(
    input: string | string[]
): NormalizedLastName {
    input = splitString(input);
    if (input.length <= 0) return makeEmptyLastName();

    const [before, after] = partitionAtFirst(input, startsWithCapital);

    if (before.length > 0 && after.length > 0) {
        const prefix = before.join(' ');
        const main = after.join(' ');
        return {
            prefix,
            main,
            full: [prefix, main].join(' '),
        };
    } else {
        const main = input.join(' ');
        return {
            prefix: null,
            main,
            full: main,
        };
    }
}

export function isUserNameFromToken(
    input: UserNameInput | null | undefined
): input is UserNameFromToken {
    return (
        typeof input === 'object' &&
        !!input &&
        (('given_name' in input && typeof input.given_name === 'string') ||
            ('family_name' in input && typeof input.family_name === 'string') ||
            ('display_name' in input && typeof input.display_name === 'string'))
    );
}

export function firstNameFromNodeInput(input: UserNameFromNode): string | null {
    if (input.first) return input.first;

    if (
        !input.last ||
        (typeof input.last === 'object' && !input.last.main && !input.last.full)
    ) {
        const full = input.full ?? input.username ?? null;
        if (full) {
            const { first } = splitFullNameString(full);
            return first;
        }
    }

    return null;
}

function lastNameFromNodeInput(input: UserNameFromNode): NormalizedLastName {
    if (typeof input.last === 'string') return splitLastNameString(input.last);

    if (!input.last) {
        const full = input.full ?? input.username ?? null;
        if (full) {
            const { last } = splitFullNameString(full);
            return last;
        } else {
            return makeEmptyLastName();
        }
    }

    if (input.last.main) {
        return {
            prefix: input.last.prefix ?? null,
            main: input.last.main ?? null,
            full: [input.last.prefix, input.last.main]
                .filter((x) => !!x)
                .join(' '),
        };
    }

    if (input.last.full) {
        return splitLastNameString(input.last.full);
    }

    const full = input.full ?? input.username ?? null;
    if (full) {
        const { last } = splitFullNameString(full);
        return last;
    }

    return {
        prefix: input.last.prefix ?? null,
        main: null,
        full: input.last.prefix ?? null,
    };
}

/**
 * Normalizes a user name.
 *
 * @param input The input username.
 */
export function normalizeUserName(input: UserNameInput): NormalizedUserName {
    if (!input) return makeEmptyUserName();
    if (typeof input === 'string') return splitFullNameString(input);

    if (isNormalizedUserName(input)) return input;

    if (isUserNameFromToken(input)) {
        input = {
            first: input.given_name,
            last: input.family_name,
            username: input.display_name,
        };
    }

    const first = firstNameFromNodeInput(input);
    const last = lastNameFromNodeInput(input);

    const full = input.full ?? [first, last.full].filter((x) => !!x).join(' ');
    const short = input.short ?? first ?? last.full;
    const username = input.username ?? full;

    return { first, last, full, short, username };
}
